import { useForm } from 'react-hook-form';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import queryString from 'query-string';
import isEqual from 'lodash.isequal';
import {
  ChangeLog,
  Pagination,
  PermissionEnum,
  ResponseError,
  responseErrorsResolver,
} from '@ui/src';
import {
  EditNotificationsValues,
  NotificationCenterFilters,
  NotificationItem,
} from '../types';
import {
  useLazyGetReportsNotificationsListQuery,
  useLazyGetReportsNotificationItemCommentQuery,
  useAddReportsNotificationItemCommentMutation,
  useUpdateReportsNotificationIssueMutation,
  useLazyGetReportsNotificationChangeLogsQuery,
  useBatchAddReportsNotificationItemCommentMutation,
  useBatchUpdateReportsNotificationIssueMutation,
  useUpdateReportsNotificationIssueCommentMutation,
} from '../store/services';

const useRequests = ({
  defaultFiltersValue,
  selectedNotification,
  pagination,
  search,
  activePermissions,
  changeLogs,
  commentsListAvailable,
  withoutCategoryFilter,
  selectedNotifications,
  setSelectedNotifications,
  selectedNotificationId,
}: {
  defaultFiltersValue: NotificationCenterFilters;
  selectedNotification?: NotificationItem;
  pagination?: Pagination;
  search?: string;
  activePermissions?: PermissionEnum[];
  changeLogs?: ChangeLog[];
  commentsListAvailable?: boolean;
  withoutCategoryFilter?: boolean;
  selectedNotifications?: string[];
  setSelectedNotifications?: (data: string[]) => void;
  selectedNotificationId?: string;
}) => {
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState('');
  const [nextPageIsLoading, setNextPageIsLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [selectedActions, setSelectedActions] = useState<string[]>([]);
  const [deadlineFilter, setDeadlineFilter] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedExecutorId, setSelectedExecutorId] = useState<string[]>([]);
  const [panelIsVisible, setPanelIsVisible] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedDeadline, setSelectedDeadline] = useState('');
  const [prevSelectedNotification, setPrevSelectedNotification] = useState<
    NotificationItem | undefined
  >(undefined);
  const [changeLogsPanelIsOpen, setChangeLogPanelIsOpen] = useState(false);
  const [batchUpdateError, setBatchUpdateError] = useState('');
  const [editSidepanelVisible, setEditSidepanelVisible] = useState(false);
  const [
    statusesChangeNotificationVisible,
    setStatusChangeNotificationVisible,
  ] = useState(false);
  const [
    deadlineChangeNotificationVisible,
    setDeadlineChangeNotificationVisible,
  ] = useState(false);
  const [stopRequest, setStopRequest] = useState(false);
  const [defaultEditIssue, setDefaultEditIssue] =
    useState<EditNotificationsValues>();

  const [
    addNotificationComment,
    { isLoading: addCommentRequestInProgress, isSuccess },
  ] = useAddReportsNotificationItemCommentMutation();
  const [
    getChangeLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetReportsNotificationChangeLogsQuery();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [getNotificationsList, { isLoading, isFetching }] =
    useLazyGetReportsNotificationsListQuery();
  const [updateNotificationIssue, { isLoading: updateInProgress }] =
    useUpdateReportsNotificationIssueMutation();
  const [
    getNotificationsComments,
    { isLoading: commentsIsLoading, isFetching: commentsIsFetching },
  ] = useLazyGetReportsNotificationItemCommentQuery();
  const [
    batchUpdateIssue,
    { isLoading: batchUpdateInProgress, isSuccess: batchUpdateIsSuccess },
  ] = useBatchUpdateReportsNotificationIssueMutation();
  const [batchAddIssueComment, { isLoading: batchAddIssueCommentInProgress }] =
    useBatchAddReportsNotificationItemCommentMutation();
  const [
    updateIssueComment,
    {
      isLoading: updateIssueCommentInProgress,
      isSuccess: updateIssueCommentIsSuccess,
    },
  ] = useUpdateReportsNotificationIssueCommentMutation();

  const navigate = useNavigate();

  const { control, handleSubmit, reset, watch, setValue } =
    useForm<NotificationCenterFilters>({
      defaultValues: defaultFiltersValue,
    });

  const {
    control: editControl,
    handleSubmit: editHandleSubmit,
    reset: editReset,
    formState: { errors: batchErrors },
    setError: setBatchError,
    setValue: setMassEditValue,
    watch: massEditWatch,
  } = useForm<EditNotificationsValues>();

  const {
    control: editIssueControl,
    handleSubmit: editIssueHandleSubmit,
    reset: editIssueReset,
    setError,
    formState: { errors },
    watch: editIssueWatch,
    setValue: setEditIssueValue,
  } = useForm<EditNotificationsValues>({
    defaultValues: {
      status: '',
      responsibleId: '',
      executorId: '',
      deadline: '',
    },
  });

  const filterFields = watch();
  const editIssueFields = editIssueWatch();
  const massEditIssueFields = massEditWatch();

  const isDirty = Boolean(
    massEditIssueFields?.comment ||
    massEditIssueFields?.deadline ||
    massEditIssueFields?.executorId ||
    massEditIssueFields?.responsibleId ||
    massEditIssueFields?.status,
  );

  const editIssueIsDirty = !isEqual(defaultEditIssue, {
    status: editIssueFields?.status || '',
    responsibleId: editIssueFields?.responsibleId || '',
    executorId: editIssueFields?.executorId || '',
    deadline: editIssueFields?.deadline || '',
  });

  const getNotificationListAction = useCallback(async (query?: string) => {
    const response = await getNotificationsList(query || '');

    if (response) {
      setNextPageIsLoading(false);
    }
  }, []);

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getChangeLogs(
        `?take=20&entityType=ISSUE&entityType=ISSUE_COMMENT&entityId=${
          selectedNotification?.issue?.id
        }${cursor ? `&cursor=${cursor}` : ''}`,
      );
    },
    [selectedNotification],
  );

  const updateIssueCommentAction = async ({
    commentId,
    text,
  }: {
    commentId: string;
    text: string;
  }) => {
    if (updateIssueComment) {
      await updateIssueComment({
        id: selectedNotificationId as string,
        commentId,
        body: {
          content: text,
        },
      });
    }
  };

  const onSendComment = async () => {
    setCommentError('');

    const response = (await addNotificationComment({
      id: selectedNotification?.id as string,
      body: {
        content: comment,
      },
    })) as { error: ResponseError };

    if (response.error) {
      responseErrorsResolver({
        errors: response.error,
        setError: () => {},
        setGlobalError: setCommentError,
      });
    }
  };

  const onScroll = (scroll: number) => {
    if (scrollRef && scrollRef.current) {
      if (
        scroll >
          scrollRef.current.scrollHeight -
            scrollRef.current.clientHeight -
            100 &&
        pagination?.hasNextPage
      ) {
        setNextPageIsLoading(true);

        let nextSearch = search
          ?.split('&')
          ?.filter((item) => !item.includes('id'))
          ?.join('&');

        if (nextSearch?.charAt(0) !== '?') {
          nextSearch = `?${nextSearch}`;
        }

        const query = `${
          nextSearch ? `${nextSearch}&` : '?'
        }cursor=${pagination?.endCursor}`;
        getNotificationListAction(query);
      }
    }
  };

  const onEditIssueSubmit = editIssueHandleSubmit(async (values) => {
    setEditError('');

    const body: {
      status?: string;
      responsibleId?: string;
      executorId?: string;
      deadline?: string;
    } = {};

    if (values.status) {
      body.status = values.status;
    }

    if (values.responsibleId) {
      body.responsibleId = values.responsibleId;
    }

    if (values.executorId) {
      body.executorId = values.executorId;
    }

    if (values.deadline) {
      body.deadline = values.deadline;
    }

    const response = (await updateNotificationIssue({
      id: selectedNotification?.id as string,
      body,
    })) as { error: ResponseError };

    if (response.error) {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError: setEditError,
      });
    } else {
      setDefaultEditIssue({
        status: values?.status || '',
        responsibleId: values?.responsibleId || '',
        executorId: values?.executorId || '',
        deadline: values?.deadline || '',
      });
    }
  });

  const onCategoryFilterApply = () => {
    setStopRequest(false);

    let nextQuery = search
      ?.replace('?', '')
      ?.split('&')
      .filter(
        (item) =>
          !item.includes('category') &&
          !item.includes('group') &&
          !item.includes('type'),
      )
      .join('&');

    if (selectedCategories?.length > 0 && !withoutCategoryFilter) {
      const categoryQuery = `${
        nextQuery?.length ? '&' : ''
      }category=${selectedCategories?.join('&category=')}`;

      nextQuery = `${nextQuery}${categoryQuery}`;
    }

    if (selectedGroups?.length > 0) {
      const groupQuery = `${
        nextQuery?.length ? '&' : ''
      }group=${selectedGroups?.join('&group=')}`;

      nextQuery = `${nextQuery}${groupQuery}`;
    }

    if (selectedActions?.length > 0) {
      const typeQuery = `${nextQuery?.length ? '&' : ''}type=${selectedActions
        ?.map((item) => item.replace('_ACTION', ''))
        ?.join('&type=')}`;

      nextQuery = `${nextQuery}${typeQuery}`;
    }

    if (!selectedCategories?.length && filterFields?.category?.length) {
      setValue('category', []);
    }

    if (!selectedGroups?.length && filterFields?.group?.length) {
      setValue('group', []);
    }

    if (!selectedActions?.length && filterFields?.type?.length) {
      setValue('type', []);
    }

    navigate(`?${nextQuery}`);
  };

  const onApplyStatusFilter = () => {
    setStopRequest(false);

    let nextQuery = search
      ?.replace('?', '')
      ?.split('&')
      .filter((item) => !item.includes('status'))
      .join('&');

    if (selectedStatuses.length > 0) {
      const statusesQuery = `${
        nextQuery?.length ? '&' : ''
      }status=${selectedStatuses?.join('&status=')}`;

      nextQuery = `${nextQuery}${statusesQuery}`;
    }

    navigate(`?${nextQuery}`);
  };

  const onApplyExecutorFilter = () => {
    setStopRequest(false);

    let nextQuery = search
      ?.replace('?', '')
      ?.split('&')
      .filter((item) => !item.includes('executorId'))
      .join('&');

    if (selectedExecutorId?.length > 0) {
      const executorQuery = `${
        nextQuery?.length ? '&' : ''
      }executorId=${selectedExecutorId?.join('&executorId=')}`;

      nextQuery = `${nextQuery}${executorQuery}`;
    }

    navigate(`?${nextQuery}`);
  };

  const onApplyDeadlineFilter = (data: string[]) => {
    setStopRequest(false);

    setDeadlineFilter(data);

    let nextQuery = search
      ?.replace('?', '')
      ?.split('&')
      .filter(
        (item) =>
          !item.includes('deadlineFrom') && !item.includes('deadlineTo'),
      )
      .join('&');

    if (data?.length > 0) {
      const [from, to] = data;

      let deadlineFilterQuery = `${
        nextQuery?.length ? '&' : ''
      }deadlineFrom=${format(new Date(from), 'yyyy-MM-dd')}`;

      if (to) {
        deadlineFilterQuery = `${deadlineFilterQuery}&deadlineTo=${format(
          new Date(to),
          'yyyy-MM-dd',
        )}`;
      }

      nextQuery = `${nextQuery}${deadlineFilterQuery}`;
    }

    navigate(`?${nextQuery}`);
  };

  const onClearDeadlineFilter = () => {
    setStopRequest(false);

    const nextQuery = search
      ?.replace('?', '')
      ?.split('&')
      .filter(
        (item) =>
          !item.includes('deadlineFrom') && !item.includes('deadlineTo'),
      )
      .join('&');

    setDeadlineFilter([]);
    setValue('deadline', []);
    setValue('deadlineFrom', '');
    setValue('deadlineTo', '');

    navigate(`?${nextQuery}`);

    if (!nextQuery?.length) {
      getNotificationListAction();
    }
  };

  const onClearExecutorFilter = () => {
    setStopRequest(false);

    const nextQuery = search
      ?.replace('?', '')
      ?.split('&')
      .filter((item) => !item.includes('executorId'))
      .join('&');

    setSelectedExecutorId([]);
    setValue('executorId', []);

    navigate(`?${nextQuery}`);

    if (!nextQuery?.length) {
      getNotificationListAction();
    }
  };

  const onClearStatusFilter = () => {
    setStopRequest(false);

    const nextQuery = search
      ?.replace('?', '')
      ?.split('&')
      .filter((item) => !item.includes('status'))
      .join('&');

    setSelectedStatuses([]);
    setValue('status', []);

    navigate(`?${nextQuery}`);

    if (!nextQuery?.length) {
      getNotificationListAction();
    }
  };

  const onCategoryFilterClear = () => {
    setStopRequest(false);

    const nextQuery = search
      ?.replace('?', '')
      ?.split('&')
      .filter(
        (item) =>
          !item.includes('category') &&
          !item.includes('group') &&
          !item.includes('type'),
      )
      .join('&');

    setSelectedActions([]);
    setSelectedCategories([]);
    setSelectedGroups([]);

    setValue('category', []);
    setValue('group', []);
    setValue('type', []);

    navigate(`?${nextQuery}`);

    if (!nextQuery?.length) {
      getNotificationListAction();
    }
  };

  const onFiltersSubmit = handleSubmit((values) => {
    setStopRequest(false);

    const nextValues = {};

    Object.keys(values).forEach((key) => {
      if (key !== 'deadline' && key !== 'createdAt') {
        if (
          Array.isArray(values[key as keyof NotificationCenterFilters]) &&
          (values[key as keyof NotificationCenterFilters] as string[])?.length >
            0
        ) {
          // @ts-ignore
          nextValues[key] = values[key as keyof NotificationCenterFilters];
        } else if (
          values[key as keyof NotificationCenterFilters] &&
          typeof values[key as keyof NotificationCenterFilters] === 'string'
        ) {
          // @ts-ignore
          nextValues[key] = values[key as keyof NotificationCenterFilters];
        }
      }
    });

    if (selectedActions.length && !values?.type?.length) {
      setSelectedActions([]);
    }

    if (selectedCategories.length && !values?.category?.length) {
      setSelectedCategories([]);
    }

    if (selectedGroups && !values?.group?.length) {
      setSelectedGroups([]);
    }

    if (selectedExecutorId.length && !values?.executorId?.length) {
      setSelectedExecutorId([]);
    }

    if (deadlineFilter.length && !values?.deadlineFrom) {
      setDeadlineFilter([]);
    }

    if (selectedStatuses.length && !values?.status?.length) {
      setSelectedStatuses([]);
    }

    setPanelIsVisible(false);

    navigate(
      `?id=${selectedNotificationId}&${queryString.stringify(nextValues)}`,
    );
  });

  const onEditSubmit = editHandleSubmit(async (batchValues) => {
    setBatchUpdateError('');

    const body: EditNotificationsValues = {
      ids: selectedNotifications,
    };

    if (batchValues?.status) {
      body.status = batchValues.status;
    }

    if (batchValues?.responsibleId) {
      body.responsibleId = batchValues?.responsibleId;
    }

    if (batchValues?.executorId) {
      body.executorId = batchValues.executorId;
    }

    if (batchValues.deadline) {
      body.deadline = batchValues.deadline;
    }

    if (Object.keys(body)?.length > 1 && batchUpdateIssue) {
      const response = (await batchUpdateIssue(body)) as {
        error: ResponseError;
      };

      if (response.error) {
        responseErrorsResolver({
          errors: response.error,
          setError: setBatchError,
          setGlobalError: setBatchUpdateError,
        });
      }
    }

    if (batchValues.comment && batchAddIssueComment) {
      const commentResponse = (await batchAddIssueComment({
        ids: selectedNotifications as string[],
        content: batchValues.comment,
      })) as { error?: ResponseError };

      if (Object.keys(body)?.length < 2 && !commentResponse.error) {
        if (setSelectedNotifications) {
          setSelectedNotifications([]);
        }

        setEditSidepanelVisible(false);
        editReset({
          status: '',
          responsibleId: '',
          executorId: '',
          deadline: '',
          comment: '',
          ids: [],
        });
      }

      if (!commentResponse.error) {
        if (body?.ids?.find((id) => id === selectedNotificationId)) {
          getNotificationsComments(
            body?.ids?.find((id) => id === selectedNotificationId) as string,
          );
        }
      }
    }
  });

  const onEditSubmitFromControlPanel = async ({
    name,
    value,
  }: {
    name: string;
    value: string;
  }) => {
    setBatchUpdateError('');

    const body: EditNotificationsValues = {
      ids: selectedNotifications,
    };

    if (name === 'status') {
      body.status = value;
    }

    if (name === 'deadline') {
      body.deadline = value;
    }

    if (batchUpdateIssue) {
      const response = (await batchUpdateIssue(body)) as {
        error?: ResponseError;
      };

      if (name === 'status' && !response.error) {
        setStatusChangeNotificationVisible(true);
      }

      if (name === 'deadline' && !response.error) {
        setDeadlineChangeNotificationVisible(true);
      }
    }
  };

  useEffect(() => {
    if (search && !stopRequest) {
      let nextSearch = search
        .replace('?', '')
        .split('&')
        ?.filter((item) => !item.includes('id'))
        ?.join('&');

      if (nextSearch?.charAt(0) === '&') {
        nextSearch = `?${nextSearch.slice(1)}`;
      } else {
        nextSearch = `?${nextSearch}`;
      }

      getNotificationListAction(nextSearch);

      const parsed = queryString.parse(nextSearch);

      if (Object.keys(parsed)?.length > 0) {
        const nextFilters = { ...parsed };

        if (parsed.createdAtFrom) {
          nextFilters.createdAt = [parsed.createdAtFrom as string];

          if (parsed.createdAtTo) {
            nextFilters.createdAt = [
              parsed.createdAtFrom as string,
              parsed.createdAtTo as string,
            ];
          }
        } else if (!parsed.createdAtFrom) {
          nextFilters.createdAt = [];
        }

        if (parsed.deadlineFrom) {
          nextFilters.deadline = [parsed.deadlineFrom as string];

          if (parsed.deadlineTo) {
            nextFilters.deadline = [
              parsed.deadlineFrom as string,
              parsed.deadlineTo as string,
            ];
          }
        } else if (!parsed.deadlineFrom) {
          nextFilters.deadline = [];
        }

        if (parsed.category) {
          if (Array.isArray(parsed.category)) {
            if (!selectedCategories.length) {
              setSelectedCategories(parsed.category as string[]);
            }

            nextFilters.category = parsed.category;
          } else {
            nextFilters.category = [parsed.category];

            if (!selectedCategories.length) {
              setSelectedCategories([parsed.category]);
            }
          }
        } else if (!parsed.category && selectedCategories.length) {
          setSelectedCategories([]);
        }

        if (parsed.group) {
          if (Array.isArray(parsed.group)) {
            if (!selectedGroups.length) {
              setSelectedGroups(parsed.group as string[]);
            }

            nextFilters.group = parsed.group;
          } else {
            nextFilters.group = [parsed.group];

            if (!selectedGroups.length) {
              setSelectedGroups([parsed.group]);
            }
          }
        } else if (!parsed.group && selectedGroups.length) {
          setSelectedGroups([]);
        }

        if (parsed.type) {
          if (Array.isArray(parsed.type)) {
            if (!selectedActions.length) {
              setSelectedActions(parsed.type as string[]);
            }

            nextFilters.type = parsed.type;
          } else {
            nextFilters.type = [parsed.type];

            if (!selectedActions.length) {
              setSelectedActions([parsed.type]);
            }
          }
        } else if (!parsed.type && selectedActions.length) {
          setSelectedActions([]);
        }

        if (parsed.status && !selectedStatus.length) {
          if (Array.isArray(parsed.status)) {
            setSelectedStatuses(parsed.status as string[]);
          } else {
            setSelectedStatuses([parsed.status]);
          }
        } else if (!parsed.status && selectedStatus.length) {
          setSelectedStatuses([]);
        }

        if (parsed.executorId) {
          if (Array.isArray(parsed.executorId)) {
            if (!selectedExecutorId.length) {
              setSelectedExecutorId(parsed.executorId as string[]);
            }

            nextFilters.executorId = parsed.executorId;
          } else {
            nextFilters.executorId = [parsed.executorId];

            if (!selectedExecutorId.length) {
              setSelectedExecutorId([parsed.executorId]);
            }
          }
        } else if (!parsed.executorId && selectedExecutorId.length) {
          setSelectedExecutorId([]);
        }

        if (parsed.responsibleId) {
          if (Array.isArray(parsed.responsibleId)) {
            nextFilters.responsibleId = parsed.responsibleId;
          } else {
            nextFilters.responsibleId = [parsed.responsibleId];
          }
        } else if (!parsed.executorId && selectedExecutorId.length) {
          setSelectedExecutorId([]);
        }

        if (parsed.deadlineFrom && !deadlineFilter.length) {
          setDeadlineFilter([parsed.deadlineFrom as string]);

          if (parsed.deadlineTo) {
            setDeadlineFilter([
              parsed.deadlineFrom as string,
              parsed.deadlineTo as string,
            ]);
          }
        } else if (!parsed.deadlineFrom && deadlineFilter.length) {
          setDeadlineFilter([]);
        }

        reset(nextFilters);
      }
    }
  }, [search, stopRequest]);

  useEffect(() => {
    if (selectedNotification)
      setDefaultEditIssue({
        status: selectedNotification.issue?.status || '',
        responsibleId: selectedNotification.issue?.responsibleId || '',
        executorId: selectedNotification.issue?.executorId || '',
        deadline: selectedNotification.issue?.deadline || '',
      });
  }, [selectedNotification]);

  useEffect(() => {
    if (
      selectedNotification &&
      commentsListAvailable &&
      (!prevSelectedNotification ||
        prevSelectedNotification.id !== selectedNotification.id)
    ) {
      getNotificationsComments(selectedNotification.id);

      if (!prevSelectedNotification) {
        setPrevSelectedNotification(selectedNotification);
      }
    }
  }, [selectedNotification, commentsListAvailable, prevSelectedNotification]);

  useEffect(() => {
    if (
      changeLogsPanelIsOpen &&
      !changeLogs &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)
    ) {
      getChangeLogsAction();
    }
  }, [changeLogsPanelIsOpen]);

  return {
    scrollRef,
    control,
    reset,
    setValue,
    editControl,
    editHandleSubmit,
    editReset,
    isDirty,
    editIssueControl,
    editIssueReset,
    editIssueIsDirty,
    errors,
    filterFields,
    getChangeLogsAction,
    comment,
    setComment,
    commentError,
    onSendComment,
    getNotificationListAction,
    nextPageIsLoading,
    onScroll,
    editError,
    setEditError,
    onEditIssueSubmit,
    selectedCategories,
    setSelectedCategories,
    selectedGroups,
    setSelectedGroups,
    selectedActions,
    setSelectedActions,
    onCategoryFilterApply,
    deadlineFilter,
    selectedStatuses,
    setSelectedStatuses,
    selectedExecutorId,
    setSelectedExecutorId,
    onApplyStatusFilter,
    onApplyExecutorFilter,
    onApplyDeadlineFilter,
    onClearDeadlineFilter,
    onClearExecutorFilter,
    onClearStatusFilter,
    onCategoryFilterClear,
    panelIsVisible,
    setPanelIsVisible,
    onFiltersSubmit,
    selectedStatus,
    setSelectedStatus,
    setPrevSelectedNotification,
    changeLogsPanelIsOpen,
    setChangeLogPanelIsOpen,
    setBatchError,
    batchErrors,
    selectedDeadline,
    setSelectedDeadline,
    batchUpdateError,
    setBatchUpdateError,
    editSidepanelVisible,
    setEditSidepanelVisible,
    onEditSubmit,
    statusesChangeNotificationVisible,
    setStatusChangeNotificationVisible,
    deadlineChangeNotificationVisible,
    setDeadlineChangeNotificationVisible,
    onEditSubmitFromControlPanel,
    updateIssueCommentAction,
    setStopRequest,
    setEditIssueValue,
    editIssueFields,
    massEditIssueFields,
    setMassEditValue,
    addCommentRequestInProgress,
    isSuccess,
    changeLogsIsLoading,
    changeLogsIsFetching,
    isLoading,
    isFetching,
    updateInProgress,
    commentsIsLoading,
    commentsIsFetching,
    batchUpdateInProgress,
    batchUpdateIsSuccess,
    batchAddIssueCommentInProgress,
    updateIssueCommentInProgress,
    updateIssueCommentIsSuccess,
  };
};

export default useRequests;
